import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AuthEndpoints } from '@core/constants/endpoints.const';
import { IAppConfig } from '@models/app-config/app.config.model';
import {
  IChangePasswordModel,
  ICurrentUserModel,
  ILoginResponseModel,
  IResetPasswordModel,
  IUserOTPVerificationModel,
} from '@models/auth/auth.model';
import { ApiResponse } from '@models/response.model';
import { APP_CONFIG_SERIVCE } from '@services/common/appconfig.service';
import { CurrentUserService } from '@services/common/currentuser.service';
import { Observable, catchError, finalize, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpClient: HttpClient;

  constructor(
    handler: HttpBackend,
    // private httpClient: HttpClient,
    private httpWithInterceptors: HttpClient,
    public currentUserService: CurrentUserService,
    @Inject(APP_CONFIG_SERIVCE) public appConfig: IAppConfig
  ) {
    this.httpClient = new HttpClient(handler);
  }

  login = (data: any) => {
    return this.httpClient.post<ApiResponse<ILoginResponseModel>>(
      `${this.appConfig?.apiEndpoint}${AuthEndpoints.Login}`,
      data,
      {
        observe: 'response',
      }
    );
  };

  private _isLoggingOut = false;

  logout() {
     // Guard against concurrent logout calls (e.g. multiple 401s firing at once)
     if (this._isLoggingOut) {
      return of(null);
    }
    this._isLoggingOut = true;   
    return this.httpWithInterceptors
      .post<ApiResponse<any>>(AuthEndpoints.Logout, {})
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this._isLoggingOut = false;
          this.currentUserService.logout();
        })
      );
  }

  verifyOtp(responseBody: IUserOTPVerificationModel) {
    return this.httpClient.post<ApiResponse<ILoginResponseModel>>(
      `${this.appConfig?.apiEndpoint}${AuthEndpoints.VerifyOtp}`,
      responseBody
    );
  }

  forgotPassword = (emailId: string) => {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig?.apiEndpoint}${AuthEndpoints.ForgotPassword}?emailId=${emailId}`,
      null
    );
  };

  /** Returns true if the current session has a jwtToken (regardless of expiry). */
  isAuthenticated() {
    const currentUser: ICurrentUserModel = this.currentUserService.get();

    const expiresIn = new Date(currentUser?.expiration);
    const currentDate = new Date();
    return (
      !!(currentUser && currentUser?.jwtToken) &&
      currentDate &&
      expiresIn &&
      currentDate <= expiresIn
    );
  }

  /** Returns true when no valid session exists. */
  isUnAuthenticated() {
    const currentUser: ICurrentUserModel = this.currentUserService.get();
    const expiration = new Date(currentUser?.expiration);
    const currentDate = new Date();
    return (
      !currentUser ||
      (!!currentUser && !currentUser?.jwtToken) ||
      (!!currentUser && currentDate > expiration)
    );
  }

  /** Returns true when a refresh token is stored (session can be silently renewed). */
  hasRefreshToken(): boolean {
    return !!this.currentUserService.get()?.refreshToken;
  }

 
  refreshToken(): Observable<ApiResponse<ILoginResponseModel>> {
    const currentUser: ICurrentUserModel = this.currentUserService.get();
    return this.httpClient.post<ApiResponse<ILoginResponseModel>>(
      `${this.appConfig?.apiEndpoint}${AuthEndpoints.RefreshToken}`,
      { refreshToken: currentUser?.refreshToken }
    );
  }

  resetPassword(resetPassword: IResetPasswordModel) {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig?.apiEndpoint}${AuthEndpoints.ResetPassword}`,
      resetPassword,
      {
        observe: 'response',
      }
    );
  }

  resendOtp(email: string) {
    return this.httpClient.post<ApiResponse<any>>(
      `${this.appConfig?.apiEndpoint}${AuthEndpoints.ResendOTP}?email=${email}`,
      null
    );
  }

  changePassword(changePasswordModel: IChangePasswordModel) {
    return this.httpWithInterceptors.post<ApiResponse<any>>(
      AuthEndpoints.ChangePassword,
      changePasswordModel
    );
  }
}
